import { IMainNavigationItem } from '@models/IHeader';
import HeaderSubNavTab from '@molecules/HeaderSubNavTab/HeaderSubNavTab';
import updateDataLayer from '@utils/helpers/gtm';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import {
  Dispatch,
  FocusEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

interface Props {
  readonly activeMainNavigationItem?: IMainNavigationItem;
  readonly isSubNavDisplayed?: boolean;
  readonly onSetIsDesktopSubNavOpen: Dispatch<SetStateAction<boolean>>;
}

const DesktopSubNav = ({
  activeMainNavigationItem,
  isSubNavDisplayed,
  onSetIsDesktopSubNavOpen,
}: Props) => {
  const mainCategory = activeMainNavigationItem?.title || '';
  const activeSubMenuItems = activeMainNavigationItem?.subMenuItems || [];
  const desktopSubNavRef = useRef<HTMLDivElement>(null);
  const [secondLevelActiveIndex, setSecondLevelActiveIndex] = useState(0);
  const isInIFrame = useIsInIFrame();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset index when display flag is toggled
  useEffect(() => {
    setSecondLevelActiveIndex(0);
  }, [isSubNavDisplayed]);

  const handleLevelTwoItemClick = (item: string) => {
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'header_menu_level_2',
      main_category: mainCategory,
      sub_category: item,
      tertiary_category: undefined,
    });
  };

  const handleBlurOnLastSubNavItem = (event: FocusEvent<HTMLLIElement>) => {
    if (!desktopSubNavRef?.current?.contains(event.relatedTarget as Node)) {
      onSetIsDesktopSubNavOpen(false);
    }
  };

  return (
    <div
      className="absolute top-[100%] right-0 left-0 z-100"
      ref={desktopSubNavRef}
    >
      <motion.div
        key={mainCategory}
        className="h-auto bg-grey-200 outline-none"
        onHoverEnd={() => {
          onSetIsDesktopSubNavOpen(false);
        }}
      >
        <motion.ul className="top flex flex-row bg-white pt-7 pr-32 pb-7 pl-32">
          {activeSubMenuItems.map((subMenuItem, index) => {
            const { link, navigationCards, links, title = '' } = subMenuItem;
            const hasSubNavTabData =
              !!navigationCards?.length || !!links?.length;
            const isCurrentSubNavTabDisplayed =
              secondLevelActiveIndex === index &&
              hasSubNavTabData &&
              isSubNavDisplayed;

            return (
              <motion.li
                key={`level-2-${link?.name}-${title}`}
                onHoverStart={() => {
                  onSetIsDesktopSubNavOpen(true);
                  setSecondLevelActiveIndex(index);
                }}
                onClick={() => {
                  onSetIsDesktopSubNavOpen(true);
                  setSecondLevelActiveIndex(index);
                }}
                onFocus={() => {
                  onSetIsDesktopSubNavOpen(true);
                  setSecondLevelActiveIndex(index);
                }}
                onBlur={
                  index === activeSubMenuItems.length - 1
                    ? handleBlurOnLastSubNavItem
                    : undefined
                }
                className={classNames(
                  'body-two cursor-pointer pr-6',
                  secondLevelActiveIndex === index
                    ? 'text-black-100'
                    : 'text-grey-500'
                )}
              >
                {link?.url ? (
                  <a
                    href={link?.url}
                    target={isInIFrame ? '_parent' : link?.target}
                    onClick={() => {
                      handleLevelTwoItemClick(title);
                    }}
                    data-testid={`CT-01-Header-Second-Level-Nav-Category-Link-${title}`}
                  >
                    <div
                      className="second-level-nav-title body-two whitespace-nowrap"
                      data-testid={`CT-01-Header-Second-Level-Nav-Category-Title-${title}`}
                    >
                      {title}
                    </div>
                  </a>
                ) : (
                  <span
                    {...(hasSubNavTabData
                      ? { role: 'menuitem', tabIndex: 0 }
                      : {})}
                    className="second-level-nav-title body-two inline-block whitespace-nowrap"
                    data-testid={`CT-01-Header-Second-Level-Nav-Category-Title-${title}`}
                  >
                    {title}
                  </span>
                )}

                {secondLevelActiveIndex === index ? (
                  <div className="mx-auto h-2 w-2 rounded-full bg-yellow" />
                ) : null}

                <div
                  className={classNames({
                    hidden: !isCurrentSubNavTabDisplayed,
                  })}
                >
                  <HeaderSubNavTab
                    activeTab={subMenuItem}
                    mainCategory={mainCategory}
                    subCategory={title}
                    hasAnimatedNavCardName={isCurrentSubNavTabDisplayed}
                    dataTestId={`CT-01-Header-Sub-Nav-Tab-${title}`}
                  />
                </div>
              </motion.li>
            );
          })}
        </motion.ul>
      </motion.div>
    </div>
  );
};

export default DesktopSubNav;
